/**
 * Inline-Marker im Text einer Produktseite (Spec 2026-10-12-pdp-freigabe-frontend-design.md,
 * Abschnitt 5.5). 1b und der Content-Experte setzen sie als Rohtext in eckigen Klammern -- so
 * gehen sie auch an n8n zurueck. Die Leseansicht zeigt sie als Chips, ohne den Text anzufassen:
 * Anker (`zitat`, Offsets) und die Rueckuebersetzung sehen nur Textknoten.
 */

export type MarkerArt = 'icon' | 'bild' | 'platzhalter' | 'pruefhinweis';

/** Die Namen vor dem Doppelpunkt als Regex-Alternative. `rueckuebersetzung.ts` baut darauf. */
export const MARKER_NAMEN = 'Icon-Wunsch|Bildwunsch|Platzhalter|Prüfhinweis|Pruefhinweis';

const ART_VON: Record<string, MarkerArt> = {
  'Icon-Wunsch': 'icon',
  Bildwunsch: 'bild',
  Platzhalter: 'platzhalter',
  Prüfhinweis: 'pruefhinweis',
  Pruefhinweis: 'pruefhinweis',
};

// Kein "]" und kein Zeilenumbruch im Marker: So steht ein Marker immer in EINEM Textknoten.
const MARKER_QUELLE = String.raw`\[(${MARKER_NAMEN}):[^\]\n]*\]`;

// Dasselbe Muster, mit dem n8n vor dem Aufruf von 7 entfernt (ohne den fuehrenden Leerraum und
// den optionalen Escape-Backslash): Gezaehlt wird genau das, was dort verschwindet. Es endet am
// Zeilenende -- ein Hinweis ohne schliessende Klammer ist keiner und reisst keine Absaetze mit.
const PRUEFHINWEIS_QUELLE = String.raw`\[Pr(?:ue|ü)fhinweis:[^\]\n]*\]`;

export interface MarkerTreffer {
  art: MarkerArt;
  /** Der Markertext samt Klammern. */
  text: string;
  von: number;
  bis: number;
}

/** Je Aufruf ein frisches RegExp-Objekt -- ein geteiltes globales haette einen `lastIndex`. */
export function markerTreffer(text: string): MarkerTreffer[] {
  const treffer: MarkerTreffer[] = [];
  for (const t of text.matchAll(new RegExp(MARKER_QUELLE, 'g'))) {
    const art = t[1] === undefined ? undefined : ART_VON[t[1]];
    if (!art) continue;
    treffer.push({ art, text: t[0], von: t.index, bis: t.index + t[0].length });
  }
  return treffer;
}

export function zaehlePruefhinweise(text: string): number {
  return text.match(new RegExp(PRUEFHINWEIS_QUELLE, 'g'))?.length ?? 0;
}

/** Code zeigt Rohtext -- ein Chip darin waere eine Behauptung, die der Text nicht macht. */
const OHNE_CHIPS = 'code, pre';

/**
 * Wickelt jeden Marker in `<span data-marker="<art>">`. Der Span enthaelt GENAU den Markertext
 * samt Klammern als einen Textknoten; die Klammern treten hoechstens per CSS zurueck.
 *
 * Bewusst NACH `alsHtml` und per DOM-API, nicht ueber die DOMPurify-Allowlist: Die erlaubt
 * weiterhin weder `span` noch `class` (`als-html.ts`). Sonst liesse sie auch Spans aus dem
 * Sprachmodell durch, mit beliebigen Klassen -- genug, um EDS-Bedienelemente auf Fremdinhalt zu
 * malen. Hier entstehen Spans nur aus unserem Code, mit einem Attributwert aus einer
 * geschlossenen Menge, und der Markertext geht als Textknoten hinein; beim Serialisieren
 * escaped der Browser `<` und `&`.
 *
 * Ohne Marker kommt dieselbe Zeichenkette zurueck: Bloecke ohne Marker behalten ihr HTML
 * unveraendert.
 */
export function mitMarkerChips(html: string): string {
  if (!html.includes('[')) return html;
  const vorlage = document.createElement('template');
  vorlage.innerHTML = html;
  // Knoten aus `document` werden beim Einhaengen in den Template-Inhalt adoptiert.
  const lauf = document.createTreeWalker(vorlage.content, NodeFilter.SHOW_TEXT);
  const textknoten: Text[] = [];
  let knoten: Node | null;
  while ((knoten = lauf.nextNode())) textknoten.push(knoten as Text);
  let gewickelt = false;
  for (const text of textknoten) {
    if (text.parentElement?.closest(OHNE_CHIPS)) continue;
    const wert = text.nodeValue ?? '';
    const treffer = markerTreffer(wert);
    if (treffer.length === 0) continue;
    const teile = document.createDocumentFragment();
    let pos = 0;
    for (const marker of treffer) {
      if (marker.von > pos) teile.append(wert.slice(pos, marker.von));
      const chip = document.createElement('span');
      chip.setAttribute('data-marker', marker.art);
      chip.textContent = marker.text;
      teile.append(chip);
      pos = marker.bis;
    }
    if (pos < wert.length) teile.append(wert.slice(pos));
    text.replaceWith(teile);
    gewickelt = true;
  }
  return gewickelt ? vorlage.innerHTML : html;
}
